import { useMemo, useState } from "react"
import {
  Block,
  Button,
  Discord,
  HeadingXSmall,
  Icon,
  ParagraphMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useGetCurrentAccount } from "@mezo-org/passport"

import accountIcon from "#/assets/account-icon.png"
import { selectTotalMats, selectUsername, useDappSelector } from "#/redux-state"
import {
  ENABLE_DISCORD_ONBOARDING,
  ENABLE_MATS_REWARDS_FOR_SIGNUP,
} from "#/shared/constants"
import { useOnboardingWallet } from "#/shared/hooks"

import OnboardingModalContent from "./OnboardingModalContent"
import OnboardingModalLinkDiscord from "./OnboardingModalLinkDiscord"
import OnboardingModalLinkedAccountIndicator from "./OnboardingModalLinkedAccountIndicator"
import OnboardingModalLinkedAccount, {
  LinkedPortalAccount,
} from "./OnboardingModalLinkedAccounts"

type OnboardingModalAccountCreatedProps = {
  closeModal: () => void
}

export default function OnboardingModalAccountCreated({
  closeModal,
}: OnboardingModalAccountCreatedProps) {
  const [isLinkDiscordOpen, setIsLinkDiscordOpen] = useState(true)

  const totalMats = useDappSelector(selectTotalMats)
  const username = useDappSelector(selectUsername)

  const { walletIcon, walletName, walletBalance } = useOnboardingWallet()
  const { data: currentAccount } = useGetCurrentAccount()

  const [css, theme] = useStyletron()

  const linkedDiscordAccount: LinkedPortalAccount | null = useMemo(() => {
    if (!currentAccount || !currentAccount.linkedAccounts) return null

    const discordAccount = currentAccount.linkedAccounts.find(
      (account) => account.type === "discord",
    )

    return discordAccount &&
      "avatarUrl" in discordAccount &&
      "name" in discordAccount
      ? {
          artwork: discordAccount.avatarUrl,
          title: "Discord",
          description: discordAccount.name,
          isLinked: true,
          indicator: (
            <OnboardingModalLinkedAccountIndicator
              color="#5865F2"
              Icon={Discord}
            />
          ),
        }
      : null
  }, [currentAccount])

  const linkedAccounts = useMemo(
    () => [
      ...(ENABLE_MATS_REWARDS_FOR_SIGNUP
        ? [
            {
              artwork: accountIcon,
              title: username,
              description: `${totalMats} mats`,
            },
          ]
        : []),
      {
        artwork: walletIcon,
        title: walletName,
        description: walletBalance,
        isLinked: true,
      },
      ...(linkedDiscordAccount ? [linkedDiscordAccount] : []),
    ],
    [
      username,
      totalMats,
      walletIcon,
      walletName,
      walletBalance,
      linkedDiscordAccount,
    ],
  )

  return (
    <OnboardingModalContent
      header="You made it!"
      footer={
        <Button width="100%" size="large" onClick={closeModal}>
          Open app
        </Button>
      }
    >
      <Block
        className={css({
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        {/* TODO: Replace it with corrected variation of CheckCircle icon */}
        <Icon size={64} viewBox="0 0 56 56" fill="none">
          <path
            d="M28.0004 53.5999C42.1389 53.5999 53.6004 42.1384 53.6004 27.9999C53.6004 13.8614 42.1389 2.3999 28.0004 2.3999C13.8619 2.3999 2.40039 13.8614 2.40039 27.9999C2.40039 42.1384 13.8619 53.5999 28.0004 53.5999Z"
            fill="black"
            stroke="#06C167"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16.4804 27.9999L24.1604 35.6799L39.5204 20.3199M53.6004 27.9999C53.6004 42.1384 42.1389 53.5999 28.0004 53.5999C13.8619 53.5999 2.40039 42.1384 2.40039 27.9999C2.40039 13.8614 13.8619 2.3999 28.0004 2.3999C42.1389 2.3999 53.6004 13.8614 53.6004 27.9999Z"
            fill="black"
            stroke="#AEDDC2"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Icon>
        <HeadingXSmall
          marginTop={theme.sizing.scale600}
          marginBottom={theme.sizing.scale500}
        >
          Welcome to Mezo, <br /> {username}
        </HeadingXSmall>
        <ParagraphMedium marginBottom={theme.sizing.scale1200}>
          Glad to have you here! Let&apos;s get started.
        </ParagraphMedium>
        <OnboardingModalLinkedAccount accounts={linkedAccounts} />
      </Block>
      <OnboardingModalLinkDiscord
        isOpen={ENABLE_DISCORD_ONBOARDING && isLinkDiscordOpen}
        onClose={() => setIsLinkDiscordOpen(false)}
      />
    </OnboardingModalContent>
  )
}
